import { PieChart, Pie, Sector, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import React, { PureComponent, useCallback } from 'react';

//GraphQL
import { useGetChipsSummaryQuery, useTerminalGainLossQuery, useTotalCashierCashQuery } from '@/generated/graphql';

import { mapGQLChipsSummary, IChipsSummary } from '@/shared/interfaces';

// material ui
import { Card, Divider, Grid, Stack, Typography } from '@mui/material';

//interfaces
import type { GameReport } from '@/shared/interfaces';
import { useState } from 'react';

interface IProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
  payload: {
    name: string;
  };
  percent: number;
  value: number;
}

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <g>
      <text x={cx} y={cy} dy={-1} textAnchor="middle" fill="#333" fontSize={14} fontWeight={700}>
        {value}
      </text>
      <text x={cx} y={cy} dy={12} textAnchor="middle" fill="#999">
        {payload.name}
      </text>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
    </g>
  );
};

interface IProps {
  gameReport: GameReport | undefined;
}

export const ChipsSummary: React.FC = () => {
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  const [chipsSummary, setChipsSummary] = useState([
    { value: 0, name: '0' },
    { value: 0, name: '0' },
    { value: 0, name: '0' },
    { value: 0, name: '0' },
  ]);
  const [totalCashierCash, setTotalCashierCash] = useState<number>(0);
  const [terminalGainLoss, setTerminalGainLoss] = useState<number>(0);
  const [totalChips, setTotalChips] = useState<number>(0);

  useGetChipsSummaryQuery({
    onCompleted(data) {
      const mappedData = mapGQLChipsSummary(data.chipsSummary);

      setTimeout(() => {
        const chipsSummary = [
          { value: mappedData.cashiersChips, name: 'Cashier Chips' },
          { value: mappedData.playerChips, name: 'Player Chips' },
          { value: mappedData.terminalChips, name: 'Terminal Chips' },
          { value: mappedData.usherChips, name: 'Usher Chips' },
        ];
        setChipsSummary(chipsSummary);
        setTotalChips(chipsSummary.map((x) => x.value).reduce((a, b) => a + b));
      }, 100);
    },
  });

  useTerminalGainLossQuery({
    onCompleted(data) {
      setTimeout(() => {
        setTerminalGainLoss(data.terminalGainLoss);
      }, 100);
    },
  });

  useTotalCashierCashQuery({
    onCompleted(data) {
      setTimeout(() => {
        setTotalCashierCash(data.totalCashierCash);
      }, 100);
    },
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const onPieEnter = useCallback(
    (_: any, index: number) => {
      setActiveIndex(index);
    },
    [setActiveIndex],
  );

  return (
    <>
      <Card sx={{ background: '#fff', py: 3, fontFamily: 'Source Sans Pro,Hiragino Sans GB,Arial,sans-serif;', fontSize: '12px' }}>
        <Grid container spacing={2}>
          <Grid size={{ md: 12, xl: 6 }}>
            <PieChart width={230} height={200}>
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={chipsSummary}
                cx={100}
                cy={100}
                innerRadius={50}
                outerRadius={70}
                fill="#8884d8"
                dataKey="value"
                onMouseEnter={onPieEnter}>
                {chipsSummary.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>

              <Tooltip />
            </PieChart>
          </Grid>
          <Grid size={{ md: 12, xl: 6 }}>
            <Stack direction={'column'} height={'100%'}>
              <Stack direction={'column'} spacing={1} my={'auto'} mx={2}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Typography variant="body2">Cashier Chips</Typography>
                  <Typography variant="body2">{chipsSummary[0].value}</Typography>
                </Stack>
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Typography variant="body2">Player Chips</Typography>
                  <Typography variant="body2">{chipsSummary[1].value}</Typography>
                </Stack>
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Typography variant="body2">Usher Chips</Typography>
                  <Typography variant="body2">{chipsSummary[3].value}</Typography>
                </Stack>
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Typography variant="body2">Terminal Chips</Typography>
                  <Typography variant="body2">{chipsSummary[2].value}</Typography>
                </Stack>
                <Divider />
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Typography variant="body2">Total Chips</Typography>
                  <Typography variant="body2">{totalChips}</Typography>
                </Stack>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        <Stack direction={'row'} alignItems={'center'} spacing={2}></Stack>
      </Card>
      <Card sx={{ background: '#87add4', py: 2, mt: 2, px: 3 }}>
        <Typography variant="h6" fontWeight={600} lineHeight={1}>
          {totalCashierCash}
        </Typography>
        <Typography variant="overline" sx={{ display: 'block', lineHeight: 1.66, fontSize: '9px' }}>
          Cashier Cash
        </Typography>
      </Card>
      <Card sx={{ background: '#87add4', py: 2, mt: 2, px: 3 }}>
        <Typography variant="h6" fontWeight={600} lineHeight={1}>
          {terminalGainLoss}
        </Typography>
        <Typography variant="overline" sx={{ display: 'block', lineHeight: 1.66, fontSize: '9px' }}>
          Terminal Gain Loss
        </Typography>
      </Card>
    </>
  );
};
